
import React, { createContext, useContext, useState, useEffect } from 'react';
import { DateRange } from 'react-day-picker';
import { startOfMonth, endOfMonth } from 'date-fns';
import { useLocation, useNavigate } from 'react-router-dom';

interface WorkHistoryContextType {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  activeFilter: string;
  setActiveFilter: (filter: string) => void;
  selectedLogId: string | null;
  setSelectedLogId: (id: string | null) => void;
  isLogDialogOpen: boolean;
  setIsLogDialogOpen: (isOpen: boolean) => void;
  isDetailDialogOpen: boolean;
  setIsDetailDialogOpen: (isOpen: boolean) => void;
  activeDetailTab: string;
  setActiveDetailTab: (tab: string) => void;
  commentRefreshTrigger: number;
  refreshComments: () => void;
  dateRange: DateRange;
  setDateRange: React.Dispatch<React.SetStateAction<DateRange>>;
  selectedMonth: string | null;
  setSelectedMonth: (month: string | null) => void;
  isDatePickerOpen: boolean;
  setIsDatePickerOpen: (isOpen: boolean) => void;
  preventDetailDialogToggle: boolean;
  setPreventDetailDialogToggle: (value: boolean) => void;
  clearUrlParams: () => void;
}

const WorkHistoryContext = createContext<WorkHistoryContextType | undefined>(undefined);

export const WorkHistoryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('Este mes');
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
  const [isLogDialogOpen, setIsLogDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState('details');
  const [commentRefreshTrigger, setCommentRefreshTrigger] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [preventDetailDialogToggle, setPreventDetailDialogToggle] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();
  
  // Initialize with current month date range
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const now = new Date();
    return {
      from: startOfMonth(now),
      to: endOfMonth(now)
    };
  });

  // Function to clear URL parameters while staying on the same page
  const clearUrlParams = () => {
    navigate(location.pathname, { replace: true });
  };

  // Check for log ID in URL and open the detail dialog if needed - only on initial load
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const logId = searchParams.get('log');
    
    if (logId) {
      setSelectedLogId(logId);
      setIsDetailDialogOpen(true);
      
      // If coming from notification, set flag to prevent toggle behavior
      if (location.state?.fromNotification) {
        setPreventDetailDialogToggle(true);
        
        // Reset after a short delay
        setTimeout(() => {
          setPreventDetailDialogToggle(false);
        }, 1000);
      }
    }
  }, []); // Empty dependency array means this only runs once on mount

  // Close detail dialog handler that also cleans up URL
  useEffect(() => {
    if (!isDetailDialogOpen && selectedLogId && location.search.includes('log=')) {
      // Clean up URL when dialog is closed
      clearUrlParams();
      // Reset selected log id after cleanup
      setSelectedLogId(null);
    }
  }, [isDetailDialogOpen, location.search, selectedLogId]);

  // Update date range when filter changes, but only if it's a standard filter
  useEffect(() => {
    const now = new Date();
    
    if (activeFilter === 'Este mes') {
      setDateRange({
        from: startOfMonth(now),
        to: endOfMonth(now)
      });
    } else if (activeFilter === 'Mes anterior') {
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1);
      setDateRange({
        from: startOfMonth(lastMonth),
        to: endOfMonth(lastMonth)
      });
    } else if (activeFilter === 'Todo') {
      setDateRange({ from: undefined, to: undefined });
    }
    // We don't update the date range for custom filters or month selections
  }, [activeFilter]);

  const refreshComments = () => {
    setCommentRefreshTrigger(prev => prev + 1);
  };

  const value = {
    searchTerm,
    setSearchTerm,
    activeFilter,
    setActiveFilter,
    selectedLogId,
    setSelectedLogId,
    isLogDialogOpen,
    setIsLogDialogOpen,
    isDetailDialogOpen,
    setIsDetailDialogOpen,
    activeDetailTab,
    setActiveDetailTab,
    commentRefreshTrigger,
    refreshComments,
    dateRange,
    setDateRange,
    selectedMonth,
    setSelectedMonth,
    isDatePickerOpen,
    setIsDatePickerOpen,
    preventDetailDialogToggle,
    setPreventDetailDialogToggle,
    clearUrlParams
  };

  return (
    <WorkHistoryContext.Provider value={value}>
      {children}
    </WorkHistoryContext.Provider>
  );
};

export const useWorkHistory = (): WorkHistoryContextType => {
  const context = useContext(WorkHistoryContext);
  if (context === undefined) {
    throw new Error('useWorkHistory must be used within a WorkHistoryProvider');
  }
  return context;
};
